import { useEffect, useMemo, useState } from "react";
import UtilizationExpenseDonut from "./UtilizationExpenseDonut";
import UtilizationBudgetInfoIcon from "./UtilizationBudgetInfoIcon";
import UtilizationExpenseInfoIcon from "./UtilizationExpenseInfoIcon";
import {
  computeCardTotals,
  formatShortMonthLabel,
  formatUtilizationAmount,
  getUtilizationPercentColor,
} from "./utilizationReportUtils";

const EMPTY_TOTAL_ROW = {
  rowLabel: "Selected Record Total",
  prevFy: {
    totalBudget: 0,
    totalExpense: 0,
    totalBalance: 0,
    budgetDetails: {},
    expenseDetails: {},
  },
  selectedFy: {
    totalBudget: 0,
    expenseUptoPrevMonth: 0,
    expenseForMonth: 0,
    totalExpense: 0,
    expensePercentOfBudget: 0,
    totalBalance: 0,
    budgetDetails: {},
    expenseDetails: {},
  },
  cumulative: {
    totalBudget: 0,
    totalExpense: 0,
    totalBalance: 0,
  },
};

const getRowKey = (row, index) =>
  String(row?.rowId ?? `${row?.rowLabel || "row"}-${row?.slNo ?? index}`);

const AmountCell = ({ value, className = "" }) => (
  <td className={`utilization-card__amount ${className}`.trim()}>
    {formatUtilizationAmount(value)}
  </td>
);

const BudgetAmountCell = ({
  value,
  blockType,
  budgetDetails,
  meta,
  className = "",
}) => (
  <td className={`utilization-card__amount ${className}`.trim()}>
    <UtilizationBudgetInfoIcon
      blockType={blockType}
      totalBudget={value}
      details={budgetDetails}
      meta={meta}
    >
      {formatUtilizationAmount(value)}
    </UtilizationBudgetInfoIcon>
  </td>
);

const ExpenseAmountCell = ({
  value,
  expenseType,
  expenseDetails,
  meta,
  className = "",
}) => (
  <td className={`utilization-card__amount ${className}`.trim()}>
    <UtilizationExpenseInfoIcon
      expenseType={expenseType}
      totalExpense={value}
      details={expenseDetails}
      meta={meta}
    >
      {formatUtilizationAmount(value)}
    </UtilizationExpenseInfoIcon>
  </td>
);

const SectionHeader = ({ lines, className = "" }) => (
  <span className={`utilization-card__section-head ${className}`.trim()}>
    {lines.map((line) => (
      <span key={line}>{line}</span>
    ))}
  </span>
);

const DonutCell = ({ expense, balance, percent }) => {
  const shouldHide = Number(expense) === 0 && Number(balance) === 0;
  const { tone, color, track } = getUtilizationPercentColor(percent);

  if (shouldHide) {
    return <td className="utilization-card__donut-cell utilization-card__donut-cell--empty" />;
  }

  return (
    <td
      className={`utilization-card__donut-cell utilization-card__donut-cell--tone-${tone}`}
      style={{ backgroundColor: track }}
    >
      <UtilizationExpenseDonut percent={percent} color={color} track={track} />
    </td>
  );
};

const UtilizationReportRow = ({
  row,
  isTotal = false,
  totalVariant = "default",
  meta,
  displaySlNo,
  isSelected = false,
  isNameSelectable = false,
  onNameClick,
}) => (
  <tr
    className={
      isTotal
        ? `utilization-card__total-row${
            totalVariant === "overall"
              ? " utilization-card__total-row--overall"
              : totalVariant === "selected"
                ? " utilization-card__total-row--selected"
                : ""
          }`
        : "utilization-card__data-row"
    }
  >
    <td className="utilization-card__sticky-col utilization-card__sno">
      {isTotal ? "" : displaySlNo ?? row.slNo}
    </td>
    <td
      className={`utilization-card__sticky-col utilization-card__name${
        isTotal ? " utilization-card__total-label" : ""
      }${isNameSelectable ? " utilization-card__name--selectable" : ""}${
        isSelected ? " utilization-card__name--selected" : ""
      }`}
      title={row.rowLabel}
      onClick={isNameSelectable ? onNameClick : undefined}
      role={isNameSelectable ? "button" : undefined}
      tabIndex={isNameSelectable ? 0 : undefined}
      onKeyDown={
        isNameSelectable
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onNameClick?.();
              }
            }
          : undefined
      }
    >
      {row.rowLabel}
    </td>

    <BudgetAmountCell
      value={row.prevFy.totalBudget}
      blockType="prevFy"
      budgetDetails={row.prevFy.budgetDetails}
      meta={meta}
    />
    <ExpenseAmountCell
      value={row.prevFy.totalExpense}
      expenseType="prevFy"
      expenseDetails={row.prevFy.expenseDetails}
      meta={meta}
    />
    <AmountCell value={row.prevFy.totalBalance} />

    <BudgetAmountCell
      value={row.selectedFy.totalBudget}
      blockType="selectedFy"
      budgetDetails={row.selectedFy.budgetDetails}
      meta={meta}
    />
    <ExpenseAmountCell
      value={row.selectedFy.expenseUptoPrevMonth}
      expenseType="selectedUpto"
      expenseDetails={row.selectedFy.expenseDetails}
      meta={meta}
      className="utilization-card__col-month"
    />
    <ExpenseAmountCell
      value={row.selectedFy.expenseForMonth}
      expenseType="selectedForMonth"
      expenseDetails={row.selectedFy.expenseDetails}
      meta={meta}
      className="utilization-card__col-month"
    />
    <ExpenseAmountCell
      value={row.selectedFy.totalExpense}
      expenseType="selectedTotal"
      expenseDetails={row.selectedFy.expenseDetails}
      meta={meta}
    />
    <DonutCell
      expense={row.selectedFy.totalExpense}
      balance={row.selectedFy.totalBalance}
      percent={row.selectedFy.expensePercentOfBudget}
    />
    <AmountCell value={row.selectedFy.totalBalance} />

    <AmountCell value={row.cumulative.totalBudget} />
    <AmountCell value={row.cumulative.totalExpense} />
    <AmountCell value={row.cumulative.totalBalance} />
  </tr>
);

const UtilizationReportCard = ({
  card,
  meta,
  variant = "default",
  totalRow: totalRowProp,
  enableRowSelection = false,
}) => {
  const isGrandTotal = variant === "grand";
  const [selectedRowIds, setSelectedRowIds] = useState(() => new Set());
  const [hideUnselected, setHideUnselected] = useState(false);

  useEffect(() => {
    if (!enableRowSelection) {
      setSelectedRowIds(new Set());
      setHideUnselected(false);
      return;
    }

    setSelectedRowIds(
      new Set(card.rows.map((row, index) => getRowKey(row, index)))
    );
    setHideUnselected(false);
  }, [card.id, card.rows, enableRowSelection]);

  const toggleRowSelection = (rowKey) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) {
        next.delete(rowKey);
      } else {
        next.add(rowKey);
      }
      return next;
    });
  };

  const visibleRows = useMemo(() => {
    const rowsWithKeys = card.rows.map((row, index) => ({
      row,
      rowKey: getRowKey(row, index),
    }));

    if (!enableRowSelection || !hideUnselected) {
      return rowsWithKeys;
    }

    return rowsWithKeys.filter(({ rowKey }) => selectedRowIds.has(rowKey));
  }, [card.rows, enableRowSelection, hideUnselected, selectedRowIds]);

  const totalRow = useMemo(() => {
    if (totalRowProp) return totalRowProp;

    if (!enableRowSelection) {
      return computeCardTotals(card.rows);
    }

    const selectedRows = card.rows.filter((row, index) =>
      selectedRowIds.has(getRowKey(row, index))
    );

    return (
      computeCardTotals(selectedRows, "Selected Record Total") ||
      EMPTY_TOTAL_ROW
    );
  }, [card.rows, totalRowProp, enableRowSelection, selectedRowIds]);

  const overallTotalRow = useMemo(() => {
    if (isGrandTotal || totalRowProp || !enableRowSelection) {
      return null;
    }

    return computeCardTotals(card.rows, "Overall Total");
  }, [card.rows, enableRowSelection, isGrandTotal, totalRowProp]);

  const selectedSectionTitle = `For the month - ${meta.selectedMonthCaption || "-"} (FY ${meta.selectedFyLabel}) as of ${meta.asOnDate}`;
  const cumulativeSectionTitle = `Cumulative upto month - ${meta.selectedMonthCaption || "-"} (FY ${meta.selectedFyLabel}) as of ${meta.asOnDate}`;

  return (
    <div
      className={`utilization-card${
        isGrandTotal ? " utilization-card--grand-total" : ""
      }`}
    >
      <div className="utilization-card__title">
        {isGrandTotal ? "Grand Total" : card.title}
      </div>

      <div className="utilization-card__table-wrap">
        <table className="utilization-card__table">
          <colgroup>
            <col className="utilization-col utilization-col--sno" />
            <col className="utilization-col utilization-col--name" />
            <col span={3} className="utilization-col utilization-col--amount" />
            <col span={3} className="utilization-col utilization-col--amount" />
            <col className="utilization-col utilization-col--amount" />
            <col className="utilization-col utilization-col--donut" />
            <col className="utilization-col utilization-col--amount" />
            <col span={3} className="utilization-col utilization-col--amount" />
          </colgroup>
          <thead>
            <tr className="utilization-card__group-row">
              <th rowSpan={2} className="utilization-card__sticky-col utilization-card__group-sno">
                Sl
              </th>
              <th rowSpan={2} className="utilization-card__sticky-col utilization-card__group-name">
                {enableRowSelection ? (
                  <label
                    className={`utilization-card__hide-unselected${
                      hideUnselected
                        ? " utilization-card__hide-unselected--on"
                        : ""
                    }`}
                    title={
                      hideUnselected
                        ? "Show all records"
                        : "Hide unselected records"
                    }
                  >
                    <input
                      type="checkbox"
                      className="utilization-card__hide-unselected-input"
                      checked={hideUnselected}
                      onChange={(event) =>
                        setHideUnselected(event.target.checked)
                      }
                      aria-label="Hide unselected records"
                    />
                    <span
                      className="utilization-card__hide-unselected-track"
                      aria-hidden="true"
                    >
                      <span className="utilization-card__hide-unselected-ball" />
                    </span>
                    <span className="utilization-card__hide-unselected-text">
                      {hideUnselected ? "Selected only" : "Show all"}
                    </span>
                  </label>
                ) : (
                  meta.rowColumnLabel
                )}
              </th>
              <th colSpan={3} className="utilization-card__section utilization-card__section--prev">
                Upto previous FY ({meta.previousFyLabel})
              </th>
              <th colSpan={6} className="utilization-card__section utilization-card__section--selected">
                <SectionHeader lines={[selectedSectionTitle]} />
              </th>
              <th colSpan={3} className="utilization-card__section utilization-card__section--cumulative">
                <SectionHeader lines={[cumulativeSectionTitle]} />
              </th>
            </tr>
            <tr className="utilization-card__subhead-row">
              <th>Budget</th>
              <th>Expense</th>
              <th>Balance</th>

              <th>Budget</th>
              <th className="utilization-card__col-month">
                <SectionHeader
                  lines={[
                    "Expense upto",
                    formatShortMonthLabel(meta.previousMonthLabel),
                  ]}
                />
              </th>
              <th className="utilization-card__col-month">
                <SectionHeader
                  lines={[
                    "Expense for",
                    formatShortMonthLabel(meta.selectedMonthLabel),
                  ]}
                />
              </th>
              <th>Expense</th>
              <th className="utilization-card__col-donut">% Budget</th>
              <th>Balance</th>

              <th>Budget</th>
              <th>Expense</th>
              <th>Balance</th>
            </tr>
          </thead>
          <tbody>
            {totalRow && (
              <UtilizationReportRow
                row={totalRow}
                isTotal
                totalVariant={enableRowSelection ? "selected" : "default"}
                meta={meta}
              />
            )}
            {!isGrandTotal &&
              visibleRows.map(({ row, rowKey }, index) => (
                  <UtilizationReportRow
                    key={`${card.id}-${rowKey}`}
                    row={row}
                    meta={meta}
                    displaySlNo={index + 1}
                    isSelected={selectedRowIds.has(rowKey)}
                    isNameSelectable={enableRowSelection}
                    onNameClick={() => toggleRowSelection(rowKey)}
                  />
                ))}
            {overallTotalRow && (
              <UtilizationReportRow
                row={overallTotalRow}
                isTotal
                totalVariant="overall"
                meta={meta}
              />
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UtilizationReportCard;
